import { useState, FormEvent } from 'react';
import { TextField, Button, Typography, Alert, CircularProgress, Link as MuiLink, Box, InputAdornment } from '@mui/material';
import { Link } from 'react-router-dom';
import { useSignup } from '@/hooks/useSignup';
import { AuthLayout } from '@/components/AuthLayout';
import { validatePassword, getPasswordRequirementsText } from '@timetable/utils';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

type Step = 'form' | 'otp' | 'done';

export function SignupPage() {
  const [step, setStep] = useState<Step>('form');

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [schoolIdError, setSchoolIdError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // OTP state
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const signupMutation = useSignup();

  const validateSchoolId = (v: string) => {
    if (!v) { setSchoolIdError('School ID is required'); return false; }
    if (!/^[A-Z0-9]{8}$/.test(v)) { setSchoolIdError('Must be exactly 8 alphanumeric characters (A-Z, 0-9)'); return false; }
    setSchoolIdError(''); return true;
  };

  const validatePasswordLocal = (pwd: string, confirm: string) => {
    const result = validatePassword(pwd, confirm);
    if (!result.valid) {
      setPasswordError(result.errors.password || result.errors.confirmPassword || '');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const startCooldown = () => {
    setResendCooldown(30);
    const t = setInterval(() => {
      setResendCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; });
    }, 1000);
  };

  const sendOtp = async (managedExternally = false) => {
    if (!managedExternally) {
      setOtpLoading(true);
      setOtpError('');
    }
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('Failed to send OTP');
      setStep('otp');
      startCooldown();
    } catch {
      setOtpError('Could not send verification code. Try again.');
    } finally {
      if (!managedExternally) setOtpLoading(false);
    }
  };

  const checkEmailExists = async (emailToCheck: string): Promise<boolean> => {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/rpc/check_email_exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ p_email: emailToCheck }),
        }
      );
      if (!res.ok) return false;
      const exists = await res.json();
      return exists === true;
    } catch {
      return false;
    }
  };

  const handleFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateSchoolId(schoolId) || !validatePasswordLocal(password, confirmPassword)) return;

    setOtpLoading(true);
    setOtpError('');
    try {
      const exists = await checkEmailExists(email);
      if (exists) {
        setOtpError('An account with this email already exists. Please sign in instead.');
        return;
      }
      await sendOtp(true);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setOtpError('');
    setOtpLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ email, otp }),
      });
      const data = await res.json();
      if (!res.ok) { setOtpError(data.error ?? 'Invalid or expired code'); return; }
      // OTP verified — proceed with actual signup
      signupMutation.mutate({ email, password, name, schoolId });
    } catch {
      setOtpError('Verification failed. Try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Done state ───────────────────────────────────────────────────────────
  if (signupMutation.isSuccess) {
    return (
      <AuthLayout>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>You're all set</Typography>
        <Alert severity="success" sx={{ mt: 2 }}>
          Account created successfully. Your request is pending admin approval — you'll receive an email once approved.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          <MuiLink component={Link} to="/login" underline="hover" color="primary">Back to sign in</MuiLink>
        </Typography>
      </AuthLayout>
    );
  }

  // ── OTP step ─────────────────────────────────────────────────────────────
  if (step === 'otp') {
    return (
      <AuthLayout>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>Verify your email</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We sent a 6-digit code to <strong>{email}</strong>
        </Typography>

        {signupMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {signupMutation.error?.message || 'Signup failed. Please try again.'}
          </Alert>
        )}

        <form onSubmit={handleOtpSubmit}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Verification code</Typography>
          <TextField
            fullWidth size="small" value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456" required autoFocus
            inputProps={{ maxLength: 6, style: { letterSpacing: '6px', fontSize: '20px', textAlign: 'center' } }}
            error={!!otpError} helperText={otpError}
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: otp.length === 6 ? (
                <InputAdornment position="end">✓</InputAdornment>
              ) : null,
            }}
          />
          <Button
            fullWidth type="submit" variant="contained" size="large"
            disabled={otpLoading || signupMutation.isPending || otp.length !== 6}
          >
            {(otpLoading || signupMutation.isPending) ? <CircularProgress size={22} color="inherit" /> : 'Verify & Create Account'}
          </Button>
        </form>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Didn't receive it?{' '}
            {resendCooldown > 0 ? (
              <Typography component="span" variant="body2" color="text.disabled">
                Resend in {resendCooldown}s
              </Typography>
            ) : (
              <MuiLink
                component="button" variant="body2" underline="hover" color="primary"
                onClick={() => sendOtp()} disabled={otpLoading}
                sx={{ cursor: 'pointer', background: 'none', border: 'none', p: 0 }}
              >
                Resend code
              </MuiLink>
            )}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          <MuiLink
            component="button" variant="body2" underline="hover"
            onClick={() => { setStep('form'); setOtp(''); setOtpError(''); }}
            sx={{ cursor: 'pointer', background: 'none', border: 'none', p: 0 }}
          >
            ← Change email
          </MuiLink>
        </Typography>
      </AuthLayout>
    );
  }

  // ── Signup form ──────────────────────────────────────────────────────────
  return (
    <AuthLayout>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>Create account</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sign up to access your school timetable
      </Typography>

      {otpError && (
        <Alert severity="error" sx={{ mb: 2 }}>{otpError}</Alert>
      )}

      <form onSubmit={handleFormSubmit}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>School ID</Typography>
        <TextField
          fullWidth size="small" value={schoolId}
          onChange={(e) => { const v = e.target.value.toUpperCase(); setSchoolId(v); if (v) validateSchoolId(v); }}
          required autoComplete="off" placeholder="e.g., A3B7K9M2" autoFocus
          error={!!schoolIdError} helperText={schoolIdError || '8-character ID from your school admin'}
          inputProps={{ maxLength: 8, style: { textTransform: 'uppercase' } }}
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Full Name</Typography>
        <TextField
          fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)}
          required autoComplete="name" placeholder="Your full name" sx={{ mb: 2 }}
        />

        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Email</Typography>
        <TextField
          fullWidth size="small" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          required autoComplete="email" placeholder="you@school.edu" sx={{ mb: 2 }}
        />

        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Password</Typography>
        <TextField
          fullWidth size="small" type="password" value={password}
          onChange={(e) => { setPassword(e.target.value); if (confirmPassword) validatePasswordLocal(e.target.value, confirmPassword); }}
          required autoComplete="new-password" placeholder="••••••••"
          helperText={getPasswordRequirementsText()} sx={{ mb: 2 }}
        />

        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Confirm Password</Typography>
        <TextField
          fullWidth size="small" type="password" value={confirmPassword}
          onChange={(e) => { setConfirmPassword(e.target.value); if (password) validatePasswordLocal(password, e.target.value); }}
          required autoComplete="new-password" placeholder="••••••••"
          error={!!passwordError} helperText={passwordError} sx={{ mb: 3 }}
        />

        <Button fullWidth type="submit" variant="contained" size="large" disabled={otpLoading}>
          {otpLoading ? <CircularProgress size={22} color="inherit" /> : 'Continue'}
        </Button>
      </form>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        Already have an account?{' '}
        <MuiLink component={Link} to="/login" underline="hover" color="primary">Sign in</MuiLink>
      </Typography>
    </AuthLayout>
  );
}
